import { lowerCase, startCase } from 'lodash-es'
import React from 'react'

import { Tooltip } from '@components/common/Tooltip'
import { NumberString, PriceString, ProfitString, getNumberString } from '@fourthwall/components'
import type {
  useAnalyticsChartQuery,
  useMembershipsChartQuery,
  useMembershipsTopTiersTableQuery,
  usePaymentMethodUsageTableQuery,
  useSaleByCountryFullTableQuery,
  useShopsStatsTrafficQuery,
  useTopOfferFullTableQuery,
} from '@fourthwall/module-analytics'
import {
  AnalyticsBar,
  AnalyticsCountry,
  SUMMARY_ROW_ID,
  createChartStatsTableTotalFreeMembers,
  createChartStatsTableTotalMembers,
  createTopProductsByUnitsSoldData,
  getPaymentMethodLabel,
} from '@fourthwall/module-analytics'
import { getPrice } from '@fourthwall/utils'
import type { useMembershipAccountQuery } from '@modules/Dashboard/hooks/useMembershipAccountQuery'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import type { ReportType } from '../../../../../Analytics.types'
import { OfferLink } from '../../../../../components/OfferLink/OfferLink'
import { REPORT_TYPES } from '../../../../../consts'
import type { AnalyticsParams } from '../../../../../modules/AnalyticsParams'
import { getSocialSourceLabel } from '../../../utils/getSocialSourceLabel'
import { mapTopTiersToAnalyticsRows } from '../../../utils/mapTopTiersToAnalyticsRows'
import type { AnalyticsReportStatsTable } from '../AnalyticsReport.types'
import { CostsBreakdownTooltipContent } from '../components/CostsBreakdownTooltipContent'
import { DataItemValue } from '../components/DataItemValue/DataItemValue'
import { createChartStatsTableColumns } from './createChartStatsTableColumns'
import { createChartStatsTableColumnsMembersChange } from './createChartStatsTableColumnsMembersChange'
import { createChartStatsTableColumnsTotalFreeMembers } from './createChartStatsTableColumnsTotalFreeMembers'
import { createChartStatsTableColumnsTotalMembers } from './createChartStatsTableColumnsTotalMembers'
import { createChartStatsTableConversionRates } from './createChartStatsTableConversionRates'
import { createChartStatsTableData } from './createChartStatsTableData'
import { createChartStatsTableFreeMembersChange } from './createChartStatsTableFreeMembersChange'
import { createChartStatsTableMembersChange } from './createChartStatsTableMembersChange'
import { createChartStatsTableProfit } from './createChartStatsTableProfit'
import { createChartStatsTableUniqueVisitors } from './createChartStatsTableUniqueVisitors'
import { getDateFormat } from './getDateFormat'

const getTimeHeader = (params: AnalyticsParams, timezoneAbbreviation: string) => {
  return `${params.precision === 'HOUR' ? 'Time' : 'Day'} (${timezoneAbbreviation})`
}

const getCostsBreakdownItems = (item: any) => {
  const items = [
    { label: 'Product costs', value: Math.abs(item.productCosts ?? 0) },
    { label: 'Fulfillment fees', value: Math.abs(item.fulfillmentFeesCosts ?? 0) },
    { label: 'Shipping costs', value: Math.abs(item.shippingCosts ?? 0) },
    { label: 'Taxes', value: Math.abs(item.taxesCosts ?? 0) },
    { label: 'Payment processing fees', value: Math.abs(item.paymentFeesCosts ?? 0) },
    { label: 'Memberships fees', value: Math.abs(item.membershipsFeesCosts ?? 0) },
    { label: 'Inventory storage costs', value: Math.abs(item.inventoryStorageCosts ?? 0) },
    { label: 'Manual charges', value: Math.abs(item.manualChargesCosts ?? 0) },
    { label: 'Other costs', value: Math.abs(item.otherCosts ?? 0) },
  ]

  return items.filter((item) => item.value > 0)
}

export const createStatsTable = (
  reportType: ReportType,
  params: AnalyticsParams,
  timezoneAbbreviation: string,
  analyticsChartQuery: ReturnType<typeof useAnalyticsChartQuery>['analyticsChartQuery'],
  membershipsChartQuery: ReturnType<typeof useMembershipsChartQuery>['membershipsChartQuery'],
  topOfferFullTableQuery: ReturnType<typeof useTopOfferFullTableQuery>['topOfferFullTableQuery'],
  saleByCountryFullTableQuery: ReturnType<
    typeof useSaleByCountryFullTableQuery
  >['saleByCountryFullTableQuery'],
  membershipsTopTiersTableQuery: ReturnType<
    typeof useMembershipsTopTiersTableQuery
  >['membershipsTopTiersTableQuery'],
  membershipAccountQuery: ReturnType<typeof useMembershipAccountQuery>['membershipAccountQuery'],
  paymentMethodUsageTableQuery: ReturnType<
    typeof usePaymentMethodUsageTableQuery
  >['paymentMethodUsageTableQuery'],
  shopsStatsTrafficQuery: ReturnType<typeof useShopsStatsTrafficQuery>['shopsStatsTrafficQuery'],
  ordersColumnsVisibility: {
    shopOrders: boolean
    donations: boolean
    twitchGifts: boolean
    giveawayLinks: boolean
    memberships: boolean
    membershipsTip: boolean
    membershipsLockedMessage: boolean
  },
  showCostsBreakdown?: boolean,
  showTotalProfitInTopOffers?: boolean,
): AnalyticsReportStatsTable<any> => {
  if (reportType === REPORT_TYPES.profit.slug) {
    return {
      columns: [
        {
          header: getTimeHeader(params, timezoneAbbreviation),
          render: (item) => {
            if (item.id === SUMMARY_ROW_ID) return item.day

            return (
              <TimezonedDateString
                date={item.day}
                format={getDateFormat(params.precision)}
                withTimezoneAbbreviation={false}
              />
            )
          },
        },
        {
          header: 'Gross Sales',
          tooltip:
            'This includes the total amount of money spent across all of your Fourthwall transactions (excluding any discounts)',
          render: (item) => <PriceString value={item.grossSales} />,
        },
        {
          header: 'Discounts',
          tooltip: 'This includes discounts from your promotional campaigns',
          render: (item) => <PriceString value={item.discounts} />,
        },
        {
          header: 'Costs',
          tooltip:
            'This includes all product costs, shipping costs, taxes, fulfillment costs, digital product fees, membership fees, and payment fees',
          render: (item) => {
            const hasCosts = Math.abs(item.cost) > 0

            if (!showCostsBreakdown || !hasCosts) {
              return <PriceString value={item.cost} />
            }

            const costsBreakdownItems = getCostsBreakdownItems(item)

            return (
              <Tooltip
                content={<CostsBreakdownTooltipContent items={costsBreakdownItems} />}
                placement="bottom-end"
              >
                <div style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                  <PriceString value={item.cost} />
                </div>
              </Tooltip>
            )
          },
        },
        {
          header: 'Cancels',
          tooltip: 'This includes all canceled transactions',
          render: (item) => <PriceString value={item.cancellations} />,
        },
        {
          header: 'Refunds',
          tooltip: 'This includes any transaction that was refunded by you but was not canceled',
          render: (item) => <PriceString value={item.refunds} />,
        },
        {
          header: 'Profit',
          render: (item) => <ProfitString value={item.profit} />,
        },
      ],
      data: createChartStatsTableProfit(analyticsChartQuery.data?.current, params),
      emptyMessage: 'No contributions found in this date range.',
      isLoading: analyticsChartQuery.isLoading,
      getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
    }
  }

  if (reportType === REPORT_TYPES['memberships-total-members'].slug) {
    return {
      columns: createChartStatsTableColumnsTotalMembers(params),
      data: createChartStatsTableTotalMembers(membershipsChartQuery.data?.current, params),
      emptyMessage: 'No data found in this date range.',
      isLoading: membershipsChartQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['memberships-total-free-members'].slug) {
    return {
      columns: createChartStatsTableColumnsTotalFreeMembers(params),
      data: createChartStatsTableTotalFreeMembers(membershipsChartQuery.data?.current, params),
      emptyMessage: 'No data found in this date range.',
      isLoading: membershipsChartQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['memberships-members-change'].slug) {
    return {
      columns: createChartStatsTableColumnsMembersChange(params),
      data: createChartStatsTableMembersChange(membershipsChartQuery.data?.current, params),
      emptyMessage: 'No data found in this date range.',
      isLoading: membershipsChartQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['memberships-free-members-change'].slug) {
    return {
      columns: createChartStatsTableColumnsMembersChange(params),
      data: createChartStatsTableFreeMembersChange(membershipsChartQuery.data?.current, params),
      emptyMessage: 'No data found in this date range.',
      isLoading: membershipsChartQuery.isLoading,
    }
  }

  if (reportType === 'orders') {
    return {
      columns: [
        {
          header: getTimeHeader(params, timezoneAbbreviation),
          render: (item) => {
            if (item.id === SUMMARY_ROW_ID) return item.day

            return (
              <TimezonedDateString
                date={item.day}
                format={getDateFormat(params.precision)}
                withTimezoneAbbreviation={false}
              />
            )
          },
        },
        {
          header: 'Shop Orders',
          render: (item) => item.contributionOrders,
          hidden: !ordersColumnsVisibility.shopOrders,
        },
        {
          header: 'Donations',
          render: (item) => item.contributionDonations,
          hidden: !ordersColumnsVisibility.donations,
        },
        {
          header: 'Twitch Gifts',
          render: (item) => item.contributionGiveaway,
          hidden: !ordersColumnsVisibility.twitchGifts,
        },
        {
          header: 'Giveaway Links',
          render: (item) => item.contributionGiftsOnCreatorCost,
          hidden: !ordersColumnsVisibility.giveawayLinks,
        },
        {
          header: 'Memberships',
          render: (item) => item.contributionSubscriptions,
          hidden: !ordersColumnsVisibility.memberships,
        },
        {
          header: 'Memberships Tip',
          render: (item) => item.contributionMembershipTips,
          hidden: !ordersColumnsVisibility.membershipsTip,
        },
        {
          header: 'Memberships Locked Message',
          render: (item) => item.contributionMembershipLockedMessages,
          hidden: !ordersColumnsVisibility.membershipsLockedMessage,
        },
        {
          header: 'Total',
          render: (item) => item.contribution,
        },
      ],
      data: createChartStatsTableData(analyticsChartQuery.data?.current, params, [
        'contributionOrders',
        'contributionDonations',
        'contributionGiveaway',
        'contributionGiftsOnCreatorCost',
        'contributionSubscriptions',
        'contributionMembershipTips',
        'contributionMembershipLockedMessages',
        'contribution',
      ]),
      emptyMessage: 'No contributions found in this date range.',
      isLoading: analyticsChartQuery.isLoading,
      getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
    }
  }

  if (reportType === REPORT_TYPES['average-order-value'].slug) {
    return {
      columns: [
        {
          header: getTimeHeader(params, timezoneAbbreviation),
          render: (item) => {
            if (item.id === SUMMARY_ROW_ID) return item.day

            return (
              <TimezonedDateString
                date={item.day}
                format={getDateFormat(params.precision)}
                withTimezoneAbbreviation={false}
              />
            )
          },
        },
        {
          header: 'Average Sales Value',
          render: (item) => <PriceString value={item.averageSales} />,
        },
        {
          header: 'Average Profit',
          render: (item) => <PriceString value={item.averageProfit} />,
        },
      ],
      data: createChartStatsTableData(analyticsChartQuery.data?.current, params, [
        'averageSales',
        'averageProfit',
      ]),
      emptyMessage: 'No contributions found in this date range.',
      isLoading: analyticsChartQuery.isLoading,
      getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
    }
  }

  if (reportType === REPORT_TYPES['top-products-by-units-sold'].slug) {
    return {
      columns: [
        {
          header: 'Product',
          render: (item) => (
            <OfferLink
              name={item.name}
              label={REPORT_TYPES['top-products-by-units-sold'].title}
              id={item.offerId}
              status={item.status}
            />
          ),
        },
        {
          header: 'Total profit',
          render: (item) => <PriceString value={item.profit || 0} />,
          hidden: !showTotalProfitInTopOffers,
        },
        {
          header: 'Units sold',
          render: (item) => <NumberString value={item.unitsSold} />,
        },
      ],
      data: createTopProductsByUnitsSoldData(
        topOfferFullTableQuery.data?.current.data,
        false,
        showTotalProfitInTopOffers,
      ),
      emptyMessage: 'No contributions found in this date range.',
      isLoading: topOfferFullTableQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['sales-by-country'].slug) {
    return {
      columns: [
        { header: 'Country', render: (item) => <AnalyticsCountry countryCode={item.country} /> },
        { header: 'Contribution', render: (item) => <NumberString value={item.contribution} /> },
        { header: 'Income', render: (item) => <ProfitString value={item.income} /> },
      ],
      data: saleByCountryFullTableQuery.data?.current.data || [],
      emptyMessage: 'No contributions found in this date range.',
      isLoading: saleByCountryFullTableQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['membership-top-tiers'].slug) {
    return {
      columns: [
        { header: 'Tier', render: (item) => item.label },
        { header: 'Supporters', render: (item) => <NumberString value={item.value} /> },
      ],
      data:
        mapTopTiersToAnalyticsRows(
          membershipsTopTiersTableQuery.data?.current.data,
          membershipAccountQuery.data?.tiers,
        ) || [],
      emptyMessage: 'No data found in this date range.',
      isLoading: membershipsTopTiersTableQuery.isLoading || membershipAccountQuery.isLoading,
    }
  }

  if (reportType === REPORT_TYPES['payment-method-used'].slug) {
    const totalValue = paymentMethodUsageTableQuery.data?.current.totalValue || 100

    return {
      columns: [
        {
          header: 'Payment method',
          render: getPaymentMethodLabel,
        },
        {
          header: 'Percentage',
          width: '70%',
          align: 'left',
          render: (item) => (
            <AnalyticsBar
              value={item.percentage}
              label={`${item.percentage}%`}
              maxValue={totalValue}
            />
          ),
        },
      ],
      data: paymentMethodUsageTableQuery.data?.current.data || [],
      totalValue,
      emptyMessage: 'No data found in this date range.',
      isLoading: paymentMethodUsageTableQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['conversion-rates'].slug) {
    return {
      columns: [
        {
          header: getTimeHeader(params, timezoneAbbreviation),
          render: (item) => {
            if (item.id === SUMMARY_ROW_ID) return item.day

            return (
              <TimezonedDateString
                date={item.day}
                format={getDateFormat(params.precision)}
                withTimezoneAbbreviation={false}
              />
            )
          },
        },
        {
          header: 'Sessions',
          render: (item) => <DataItemValue value={item.totalSessions} />,
        },
        {
          header: 'Added to cart',
          render: (item) => (
            <DataItemValue
              value={item.cartCreated.value}
              subValue={`${item.cartCreated.percentage}%`}
            />
          ),
        },
        {
          header: 'Checkout started',
          render: (item) => (
            <DataItemValue
              value={item.checkoutStarted.value}
              subValue={`${item.checkoutStarted.percentage}%`}
            />
          ),
        },
        {
          header: 'Successful checkout',
          render: (item) => (
            <DataItemValue
              value={item.checkoutPaid.value}
              subValue={`${item.checkoutPaid.percentage}%`}
            />
          ),
        },
        { header: `Conversion rate`, render: (item) => `${item.conversionRate}%` },
      ],
      getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
      data: createChartStatsTableConversionRates(
        shopsStatsTrafficQuery.data?.current.conversionRates,
      ),
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['unique-visitors'].slug) {
    return {
      columns: [
        {
          header: getTimeHeader(params, timezoneAbbreviation),
          render: (item) => {
            if (item.id === SUMMARY_ROW_ID) return item.day

            return (
              <TimezonedDateString
                date={item.day}
                format={getDateFormat(params.precision)}
                withTimezoneAbbreviation={false}
              />
            )
          },
        },
        { header: `Visitors`, render: (item) => item.visitors },
      ],
      data: createChartStatsTableUniqueVisitors(
        shopsStatsTrafficQuery.data?.current.uniqueVisitors,
      ),
      getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['sales-by-traffic-source'].slug) {
    const totalValue =
      shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.totalValue || 100

    return {
      columns: [
        {
          header: 'Traffic source',
          render: (item) => startCase(lowerCase(item.source)),
        },
        {
          header: 'Sale value',
          width: '70%',
          align: 'left',
          render: (item) => (
            <AnalyticsBar
              label={getPrice(item.income, {
                currency:
                  shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.types?.currency,
              })}
              value={item.income}
              maxValue={totalValue}
            />
          ),
        },
      ],
      data: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.data || [],
      totalValue,
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['sessions-by-traffic-source'].slug) {
    const totalValue =
      shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.totalValue || 100

    return {
      columns: [
        {
          header: 'Traffic source',
          render: (item) => startCase(lowerCase(item.source)),
        },
        {
          header: 'Sessions',
          width: '70%',
          align: 'left',
          render: (item) => (
            <AnalyticsBar
              value={item.count}
              label={getNumberString(item.count)}
              maxValue={totalValue}
            />
          ),
        },
      ],
      data: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.data || [],
      totalValue,
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['sales-by-social-source'].slug) {
    const totalValue =
      shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.totalValue || 100

    return {
      columns: [
        {
          header: 'Social source',
          render: getSocialSourceLabel,
        },
        {
          header: 'Sale value',
          width: '70%',
          align: 'left',
          render: (item) => (
            <AnalyticsBar
              value={item.income}
              label={getPrice(item.income, {
                currency:
                  shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.types?.currency,
              })}
              maxValue={totalValue}
            />
          ),
        },
      ],
      data: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.data || [],
      totalValue,
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  if (reportType === REPORT_TYPES['sessions-by-social-source'].slug) {
    const totalValue =
      shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.totalValue || 100

    return {
      columns: [
        {
          header: 'Social source',
          render: getSocialSourceLabel,
        },
        {
          header: 'Sessions',
          width: '70%',
          align: 'left',
          render: (item) => (
            <AnalyticsBar
              value={item.count}
              label={getNumberString(item.count)}
              maxValue={totalValue}
            />
          ),
        },
      ],
      data: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.data || [],
      totalValue,
      emptyMessage: 'No data found in this date range.',
      isLoading: shopsStatsTrafficQuery.isFetching,
    }
  }

  // this should never happen
  return {
    columns: createChartStatsTableColumns(params),
    data: createChartStatsTableData(analyticsChartQuery.data?.current, params, []),
    emptyMessage: 'No contributions found in this date range.',
    isLoading: analyticsChartQuery.isLoading,
    getRowBold: ({ id }) => id === SUMMARY_ROW_ID,
  }
}
